import { useEffect, useState } from 'react';
import type { Employee, EmployeeRole } from '../../types/auth';
import { ASSIGNABLE_EMPLOYEE_ROLES } from '../../types/auth';
import { roleLabels } from '../../utils/roles';
import { Modal } from '../ui/Modal';

const ASSIGNABLE_ROLES = ASSIGNABLE_EMPLOYEE_ROLES;

type ChangeRoleModalProps = {
  employee: Employee | null;
  open: boolean;
  onClose: () => void;
  onSave: (employeeId: string, role: EmployeeRole) => Promise<void>;
};

export function ChangeRoleModal({ employee, open, onClose, onSave }: ChangeRoleModalProps) {
  const [role, setRole] = useState<EmployeeRole>('compliance_officer');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (employee && open) {
      setRole(employee.role === 'ceo' ? 'compliance_officer' : employee.role);
      setError(null);
    }
  }, [employee, open]);

  const submit = async () => {
    if (!employee) return;
    setLoading(true);
    setError(null);
    try {
      await onSave(employee.id, role);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Change employee role"
      open={open && Boolean(employee)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-outline" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary btn-inline"
            disabled={loading}
            onClick={() => void submit()}
          >
            {loading ? 'Saving…' : 'Save role'}
          </button>
        </>
      }
    >
      {employee ? (
        <>
          <p className="modal-muted">{employee.name} · {employee.email}</p>
          <label className="login-label" htmlFor="emp-role-change">
            Role
          </label>
          <select
            id="emp-role-change"
            className="login-input"
            value={role}
            onChange={(e) => setRole(e.target.value as EmployeeRole)}
          >
            {ASSIGNABLE_ROLES.map((r) => (
              <option key={r} value={r}>
                {roleLabels[r] ?? r}
              </option>
            ))}
          </select>
          {error ? <p className="form-error">{error}</p> : null}
        </>
      ) : null}
    </Modal>
  );
}
